import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { parseApiError } from './apiError';
import { cacheBaseKeys } from './cacheTags';
import { encryptedData } from './encryption';
import {
  SIGNED_IN_ELSEWHERE_TEXT,
  sessionEnded,
  sessionExpired,
  tokensRefreshed,
} from './sessionEvents';

// Request description accepted by every endpoint: fetch args plus the opt-in `encrypt` flag
interface ApiRequestArgs extends FetchArgs {
  encrypt?: boolean;
}

// The part of the store this file reads; typed here so the API layer does not import the session slice
interface StateWithAuth {
  auth: { accessToken: string | null; refreshToken: string | null };
}

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BASE_API_ROUTE,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as StateWithAuth).auth.accessToken;

    if (token) {
      headers.set('Authorization', `JWT ${token}`);
    }

    return headers;
  },
});

// Request body encryption (Fernet) is opt-in: add `encrypt: true` to an endpoint's
// args and its `body` is sent to the backend as `{ encrypted: '<token>' }`.
// Currently enabled only for auth endpoints (sign-in, password, email). apisauce used
// to encrypt every POST/PUT/PATCH automatically; this is intentionally not global here,
// because the other endpoints already work with plain JSON — confirm with the backend
// before enabling it for them. FormData bodies are never encrypted.
const encryptArgs = async (
  args: string | ApiRequestArgs,
): Promise<string | FetchArgs> => {
  if (typeof args === 'string' || !args.encrypt) return args;

  const { encrypt, ...rest } = args;

  if (rest.body == null || rest.body instanceof FormData) return rest;

  return { ...rest, body: await encryptedData(rest.body) };
};

type BaseQueryApi = Parameters<typeof baseQuery>[1];
type BaseQueryExtraOptions = Parameters<typeof baseQuery>[2];

const getAuth = (api: BaseQueryApi) => (api.getState() as StateWithAuth).auth;

// Exchanges the refresh token for a new pair. Resolves to `true` when the new tokens are stored.
const refreshTokens = async (
  api: BaseQueryApi,
  extraOptions: BaseQueryExtraOptions,
): Promise<boolean> => {
  const { refreshToken } = getAuth(api);

  if (!refreshToken) {
    api.dispatch(sessionExpired());
    return false;
  }

  const refreshResult = await baseQuery(
    {
      url: 'auth/token/refresh/',
      method: 'POST',
      body: { refresh: refreshToken },
    },
    api,
    extraOptions,
  );

  if (!refreshResult?.data) {
    api.dispatch(sessionExpired());

    // The backend allows one session per account; signing in elsewhere ends this one.
    // The 401 carries no distinct code for this case, only this text, which is what tells it
    // apart from an expired token. Unlike other messages it is not translated: it is the same
    // English text whatever the user's language, so the check works for everyone.
    const error = parseApiError(refreshResult.error);
    if (error?.message.includes(SIGNED_IN_ELSEWHERE_TEXT)) {
      api.dispatch(sessionEnded(error.message));
    }

    return false;
  }

  const tokens = refreshResult.data as { access: string; refresh?: string };

  // The backend rotates the refresh token and blacklists the old one,
  // so the new one has to be stored as well
  api.dispatch(
    tokensRefreshed({
      accessToken: tokens.access,
      refreshToken: tokens.refresh ?? refreshToken,
    }),
  );

  return true;
};

// The refresh in flight, shared by every request that gets a 401 meanwhile.
// Each refresh blacklists the previous tokens, so parallel refreshes would race.
let refreshInFlight: Promise<boolean> | null = null;

const refreshTokensOnce = (
  api: BaseQueryApi,
  extraOptions: BaseQueryExtraOptions,
) => {
  refreshInFlight ??= refreshTokens(api, extraOptions).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

const baseQueryWithReauth: BaseQueryFn<
  string | ApiRequestArgs,
  unknown,
  FetchBaseQueryError
> = async (rawArgs, api, extraOptions) => {
  const args = await encryptArgs(rawArgs);

  // Do not send a request with a token that is being replaced right now
  if (refreshInFlight) await refreshInFlight;

  const usedAccessToken = getAuth(api).accessToken;
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    const currentAccessToken = getAuth(api).accessToken;

    // Another request may have refreshed the tokens while this one was on its way
    const alreadyRefreshed =
      !!currentAccessToken && currentAccessToken !== usedAccessToken;

    if (alreadyRefreshed || (await refreshTokensOnce(api, extraOptions))) {
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(cacheBaseKeys),
  endpoints: () => ({}),
});
